/* eslint-disable camelcase */

import { NETWORK_ERROR_PATTERNS } from "common/utils/app-monitoring/log-upload-error";

const CLIENT_NOISE_PATTERNS = NETWORK_ERROR_PATTERNS.filter(
  pattern => pattern.toString() !== "/timeout/i"
);

const NOISE_ERROR_PATTERNS = [
  ...CLIENT_NOISE_PATTERNS,
  /Client Closed Request/i, // CloudFront client disconnect (status 499)
  /CanceledError/i // Axios request cancellation
];

// CloudFront WAF blocks — not actionable from app code
const CLOUDFRONT_BLOCK_PATTERN = /cloudfront/i;
// Bots hitting disallowed paths — expected noise
const ROBOTS_BLOCK_PATTERN = /robots disallow|robots\.txt/i;
// Infrastructure noise — not actionable from app code, tracked server-side
// 429 = rate-limited, 499 = client closed request
// 502/503/504 = gateway errors from Envoy/Istio/nginx during deploys and pod scaling
const IGNORED_STATUSES = new Set([429, 499, 502, 503, 504]);

const isNonEmptyString = value => typeof value === "string" && value.length > 0;

const safeJsonStringify = value => {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const toResponseBodyString = error => {
  const response = error?.response;

  if (typeof response === "string") return response;

  if (response?.data) {
    return typeof response.data === "string" ? response.data : safeJsonStringify(response.data);
  }

  if (
    response &&
    typeof response === "object" &&
    !("status" in response) &&
    !("data" in response)
  ) {
    return safeJsonStringify(response);
  }

  return "";
};

const toSearchableString = error =>
  [error?.message, error?.error, toResponseBodyString(error), error?.stack]
    .filter(isNonEmptyString)
    .join(" | ");

/**
 * Returns true if the error is worth sending to browser RUM via the notify shim.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export default function isRelevantBrowserError(error) {
  const status = error?.status ?? error?.response?.status;
  const haystack = toSearchableString(error);

  // Drop rate-limited and client-closed-request responses
  if (IGNORED_STATUSES.has(status)) return false;
  // Drop CloudFront WAF and robots-disallow 403s
  if (status === 403 && CLOUDFRONT_BLOCK_PATTERN.test(haystack)) return false;
  if (status === 403 && ROBOTS_BLOCK_PATTERN.test(haystack)) return false;

  // Report application server errors (500, 501) — 502/503/504 are filtered above by IGNORED_STATUSES
  if (typeof status === "number" && status >= 500) return true;

  // Drop network/cancellation noise that is not actionable from app code.
  // Only apply to errors without an HTTP status — a real 4xx whose response body
  // happens to contain "canceled" or "offline" must still be reported.
  if (typeof status !== "number" && NOISE_ERROR_PATTERNS.some(pattern => pattern.test(haystack)))
    return false;

  return true;
}

/* eslint-enable camelcase */
